import { MaintenanceDismissed, MaintenanceWindow, isValidMaintenanceWindow } from 'src/services/maintenanceState';
import logger from 'src/utils/logger';

/**
 * Last known maintenance status, kept per device in localStorage.
 *
 * It is what the login screen and the portal fall back to while the backend
 * does not answer, which is exactly the situation the banner exists for. The
 * entry is device info like `theme` or `language`, not user data, so
 * `resetClientState()` deliberately leaves it alone: after a logout during a
 * maintenance window the login screen must still be able to explain why.
 */
export const MAINTENANCE_STORAGE_KEY = 'df_maintenance_v1';

export interface MaintenanceSnapshot {
  window: MaintenanceWindow | null;
  /** ISO timestamp of the last successful answer, `null` before the first one. */
  fetchedAt: string | null;
  /** `server_time - Date.now()` at the last successful answer. */
  clockOffsetMs: number;
  dismissed: MaintenanceDismissed;
}

export const EMPTY_MAINTENANCE_SNAPSHOT: MaintenanceSnapshot = {
  window: null,
  fetchedAt: null,
  clockOffsetMs: 0,
  dismissed: {},
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const optionalString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

const sanitizeWindow = (value: unknown): MaintenanceWindow | null => {
  if (!isRecord(value) || typeof value.start !== 'string' || typeof value.end !== 'string') return null;
  const candidate: MaintenanceWindow = {
    start: value.start,
    end: value.end,
    message: typeof value.message === 'string' ? value.message : null,
  };
  return isValidMaintenanceWindow(candidate) ? candidate : null;
};

const sanitizeDismissed = (value: unknown): MaintenanceDismissed => {
  if (!isRecord(value)) return {};
  const dismissed: MaintenanceDismissed = {};
  const planned = optionalString(value.planned);
  if (planned) dismissed.planned = planned;
  const done = optionalString(value.done);
  if (done) dismissed.done = done;
  return dismissed;
};

/** Whatever an older app version or a foreign hand left behind becomes a valid snapshot. */
const sanitize = (value: unknown): MaintenanceSnapshot => {
  if (!isRecord(value)) return EMPTY_MAINTENANCE_SNAPSHOT;
  const offset = value.clockOffsetMs;
  return {
    window: sanitizeWindow(value.window),
    fetchedAt: optionalString(value.fetchedAt) ?? null,
    clockOffsetMs: typeof offset === 'number' && Number.isFinite(offset) ? offset : 0,
    dismissed: sanitizeDismissed(value.dismissed),
  };
};

export const readMaintenanceSnapshot = (): MaintenanceSnapshot => {
  try {
    const raw = localStorage.getItem(MAINTENANCE_STORAGE_KEY);
    if (!raw) return EMPTY_MAINTENANCE_SNAPSHOT;
    return sanitize(JSON.parse(raw));
  } catch {
    return EMPTY_MAINTENANCE_SNAPSHOT;
  }
};

export const writeMaintenanceSnapshot = (snapshot: MaintenanceSnapshot): void => {
  try {
    localStorage.setItem(MAINTENANCE_STORAGE_KEY, JSON.stringify(snapshot));
  } catch (error) {
    logger.warn('Could not persist the maintenance snapshot', error);
  }
};
